import { useState, useEffect, useCallback } from "react";
import {
  AlertTypes,
  DynamicDatagrid,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  GridColDef,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  MuiIcons,
} from "@leap4/frontend";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../interfaces/root-state.interface";
import { useAppDispatch } from "../../../store/hooks";
import { useHistory } from "react-router-dom";
import { urlService } from "../../routing/urlsService";
import ReportHeader from "../common/ReportHeader/ReportHeader";
import { AXIOS_INSTANCE } from "../../../api/axiosInstance";

const { Campaign: CampaignIcon, Add: AddIcon, Edit: EditIcon, Delete: DeleteIcon } = MuiIcons;

interface Campaign {
  id_campaign: string;
  name: string | null;
  description: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

const mapStateToProps = (state: RootState) => ({
  selectedOrganization: state.auth.selectedOrganizationId,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const formatDateForInput = (isoDate: string | null): string => {
  if (!isoDate) return "";
  return isoDate.split("T")[0];
};

const CampaignsModule = (props: PropsFromRedux) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [campaignOrgMap, setCampaignOrgMap] = useState<Record<string, { id: string; name: string }[]>>({});
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await AXIOS_INSTANCE.get("/campaigns");
      setCampaigns(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
      setError(true);
      dispatch(setAlert({
        msg: "Could not fetch campaigns",
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchCampaignAccessMap = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const [mapRes, orgsRes] = await Promise.all([
        AXIOS_INSTANCE.get("/campaign-access/map"),
        AXIOS_INSTANCE.get("/campaign-access/organizations"),
      ]);
      const rawMap: Record<string, string[]> = mapRes.data || {};
      const orgList: { id: string; name: string }[] = orgsRes.data || [];
      const orgsLookup = new Map(orgList.map((o) => [o.id, o.name]));

      const map: Record<string, { id: string; name: string }[]> = {};
      for (const [campaignId, orgIds] of Object.entries(rawMap)) {
        map[campaignId] = orgIds.map((orgId) => ({
          id: orgId,
          name: orgsLookup.get(orgId) || orgId,
        }));
      }
      setCampaignOrgMap(map);
    } catch (err) {
      console.error("Failed to fetch campaign access map:", err);
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchCampaignAccessMap();
  }, [fetchCampaigns, fetchCampaignAccessMap]);

  const handleDeleteClick = (id_campaign: string) => {
    setCampaignToDelete(id_campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    try {
      await AXIOS_INSTANCE.delete(`/campaigns/${campaignToDelete}`);
      dispatch(setAlert({
        msg: "Campaign deleted successfully",
        type: AlertTypes.SUCCESS,
        d: dispatch,
      }));
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to delete campaign";
      dispatch(setAlert({
        msg: message,
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
    }
  };

  const columns: GridColDef<Campaign>[] = [
    { field: "id_campaign", headerName: "ID (Slug)", width: 180 },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            color: "primary.main",
            cursor: "pointer",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => history.push(urlService.campaignEdit(params.row.id_campaign))}
        >
          {params.row.name || params.row.id_campaign}
        </Box>
      ),
    },
    { field: "description", headerName: "Description", width: 250, flex: 1 },
    {
      field: "organizations",
      headerName: "Organizations",
      width: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (loadingOrgs) {
          return <CircularProgress size={14} sx={{ opacity: 0.4 }} />;
        }
        const orgs = campaignOrgMap[params.row.id_campaign] || [];
        if (orgs.length === 0) {
          return (
            <Typography variant="body2" sx={{ opacity: 0.4, fontStyle: "italic" }}>
              —
            </Typography>
          );
        }
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap", overflow: "hidden", alignItems: "center", height: "100%" }}>
            {orgs.map((org) => (
              <Chip
                key={org.id}
                label={org.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Inactive"}
          color={params.row.is_active ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 120,
      renderCell: (params) => formatDateForInput(params.row.start_date),
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 120,
      renderCell: (params) => formatDateForInput(params.row.end_date),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => history.push(urlService.campaignEdit(params.row.id_campaign))}
              sx={{ opacity: 0.6, "&:hover": { opacity: 1, color: "primary.main" } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id_campaign)}
              sx={{ opacity: 0.6, "&:hover": { opacity: 1, color: "error.main" } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = campaigns.map((campaign) => ({
    id: campaign.id_campaign,
    ...campaign,
  }));

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          {
            link: urlService.campaigns(),
            text: "Campaigns",
            icon: <CampaignIcon />,
          },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ mx: 2, mb: 1 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Campaign Management</Typography>
              <Typography variant="body2" color="textSecondary">
                Create, edit, and manage your conversion campaigns.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => history.push(urlService.campaignNew())}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3 }}
              >
                Add Campaign
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ margin: "10px", borderRadius: "12px" }} elevation={1}>
            <DynamicDatagrid
              rows={rows}
              columns={columns}
              loading={loading}
              refreshFunc={fetchCampaigns}
              loadError={error}
              pageSize={25}
              rowHeight={45}
              style={{ minHeight: "70vh", width: "100%" }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{campaignToDelete}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default connector(CampaignsModule);
